import React from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
  ErrorBar,
  Cell,
} from "recharts";

const CONDITION_COLORS = [
  "#3b82f6", "#22c55e", "#f59e0b", "#ef4444", "#8b5cf6", "#ec4899",
];

const CONDITION_COLORS_LIGHT = [
  "#93c5fd", "#86efac", "#fcd34d", "#fca5a5", "#c4b5fd", "#f9a8d4",
];

const DeltaChart = ({ preStats, postStats, conditions, scaleInfo, height = 220 }) => {
  if (!preStats?.byCondition || !postStats?.byCondition) {
    return <p className="text-gray-500 text-sm">No pre/post data available</p>;
  }

  const conditionIds = conditions.map((c) => c.conditionId);
  const conditionLabels = {};
  conditions.forEach((c) => {
    conditionLabels[c.conditionId] = c.label || c.conditionId;
  });

  const chartData = conditionIds.map((cid, i) => {
    const pre = preStats.byCondition[cid];
    const post = postStats.byCondition[cid];
    const delta = (post?.mean || 0) - (pre?.mean || 0);
    return {
      name: conditionLabels[cid] || cid,
      pre: pre?.mean || 0,
      pre_sd: pre?.sd || 0,
      post: post?.mean || 0,
      post_sd: post?.sd || 0,
      delta: delta,
      colorIndex: i,
    };
  });

  const minDomain = scaleInfo?.min ?? 0;
  const maxDomain = scaleInfo?.max ?? scaleInfo?.points ?? 7;

  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    const data = payload[0]?.payload;
    return (
      <div className="bg-gray-800 border border-gray-600 rounded px-3 py-2 text-xs">
        <div className="text-white font-medium mb-1">{label}</div>
        <div className="text-blue-300">Pre: M = {data.pre?.toFixed(2)}, SD = {data.pre_sd?.toFixed(2)}</div>
        <div className="text-green-300">Post: M = {data.post?.toFixed(2)}, SD = {data.post_sd?.toFixed(2)}</div>
        <div className={`font-medium ${data.delta >= 0 ? "text-green-400" : "text-red-400"}`}>
          Delta: {data.delta >= 0 ? "+" : ""}{data.delta?.toFixed(2)}
        </div>
      </div>
    );
  };

  return (
    <ResponsiveContainer width="100%" height={height}>
      <BarChart data={chartData} margin={{ top: 15, right: 10, left: 0, bottom: 5 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
        <XAxis
          dataKey="name"
          tick={{ fill: "#9ca3af", fontSize: 10 }}
          stroke="#4b5563"
        />
        <YAxis
          domain={[minDomain, maxDomain]}
          tick={{ fill: "#9ca3af", fontSize: 11 }}
          stroke="#4b5563"
        />
        <Tooltip content={<CustomTooltip />} />
        <Legend />
        <Bar dataKey="pre" name="Pre" barSize={24}>
          {chartData.map((entry, i) => (
            <Cell key={i} fill={CONDITION_COLORS_LIGHT[entry.colorIndex % CONDITION_COLORS_LIGHT.length]} />
          ))}
          <ErrorBar dataKey="pre_sd" width={4} stroke="#9ca3af" strokeWidth={1} />
        </Bar>
        <Bar dataKey="post" name="Post" barSize={24}>
          {chartData.map((entry, i) => (
            <Cell key={i} fill={CONDITION_COLORS[entry.colorIndex % CONDITION_COLORS.length]} />
          ))}
          <ErrorBar dataKey="post_sd" width={4} stroke="#9ca3af" strokeWidth={1} />
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
};

export default DeltaChart;
